import { useState, useEffect } from 'react';
import { ServiceAttributeSummaryData } from '../models/PulseData';
import { CustomerInfo, getServicesForGroupId } from '../models/CustomerData';
import { fetchPulseServiceAttributesData } from '../services/pulseService';
import { sampleServiceAttributeData } from '../data/PulseContent.samples';
import { useCustomer } from '../contexts/CustomerContext';
import { TimeSliceWindow } from '@/models/TimeSlice';
import logger from '../utils/logger';

interface UsePulseServiceAttributesDataProps {
  selectedCustomer: CustomerInfo | null;
  selectedServicesGroupId: string | null;
  selectedServiceForAttribute: string | null;
  keyAttributes: string[];
  timeSliceWindow: TimeSliceWindow;
}

interface PulseServiceAttributesDataState {
  serviceAttributesData: Record<string, ServiceAttributeSummaryData | null>;
  isLoadingAttributes: Record<string, boolean>;
  attributeErrors: Record<string, string | null>;
}

export const usePulseServiceAttributesData = ({
  selectedCustomer,
  selectedServicesGroupId,
  selectedServiceForAttribute,
  keyAttributes,
  timeSliceWindow,
}: UsePulseServiceAttributesDataProps): PulseServiceAttributesDataState => {
  const [dataState, setDataState] = useState<PulseServiceAttributesDataState>({
    serviceAttributesData: {},
    isLoadingAttributes: {},
    attributeErrors: {},
  });

  const { selectedApiKey } = useCustomer();

  useEffect(() => {
    if (!selectedCustomer || !selectedServicesGroupId || !selectedServiceForAttribute) {
      setDataState({
        serviceAttributesData: {},
        isLoadingAttributes: {},
        attributeErrors: {},
      });
      return;
    }

    // Reset states for the new service
    const initialLoadingStates: Record<string, boolean> = {};
    keyAttributes.forEach(attr => (initialLoadingStates[attr] = true));
    setDataState({
      serviceAttributesData: {},
      isLoadingAttributes: initialLoadingStates,
      attributeErrors: {},
    });

    const params = {
      apiKey: selectedApiKey,
      customer: selectedCustomer.id,
      servicesList: getServicesForGroupId( selectedCustomer, selectedServicesGroupId),
      service: selectedServiceForAttribute,
      attributes: keyAttributes,
      startDate: timeSliceWindow.startTime,
      endDate: timeSliceWindow.endTime,
      timeIncrement: timeSliceWindow.incrementMinutes,
    };

    fetchPulseServiceAttributesData(params)
      .then(results => {
        const dataMap: Record<string, ServiceAttributeSummaryData | null> = {};
        const errorMap: Record<string, string | null> = {};
        const loadingMap: Record<string, boolean> = {};

        results.forEach(({ attribute, data, error }) => {
          dataMap[attribute] = data;
          errorMap[attribute] = error;
          loadingMap[attribute] = false;
          if (error && selectedServiceForAttribute === sampleServiceAttributeData.services && attribute === sampleServiceAttributeData.attribute) {
            // This specific fallback logic might need to be more generic or configurable if used widely
            dataMap[attribute] = sampleServiceAttributeData; // Overwrite with sample on specific error condition
            logger.warn(`Falling back to sample attribute data for service ${selectedServiceForAttribute}, attribute ${attribute} due to fetch error.`);
          }
        });

        setDataState({
          serviceAttributesData: dataMap,
          attributeErrors: errorMap,
          isLoadingAttributes: loadingMap,
        });
        logger.info(`Finished fetching attribute data for service: ${selectedServiceForAttribute}`);
      })
      .catch(err => {
        logger.error(`Unhandled error in fetchPulseServiceAttributesData promise chain for service ${selectedServiceForAttribute}:`, err);
        // Set all to not loading and potentially set a general error or clear data
        const finalLoadingStates: Record<string, boolean> = {};
        keyAttributes.forEach(attr => (finalLoadingStates[attr] = false));
        setDataState(prev => ({
            ...prev,
            isLoadingAttributes: finalLoadingStates,
            // Potentially set a general error message here if needed, or clear all attributeErrors
        }));
      });
  }, [selectedCustomer, selectedApiKey, selectedServicesGroupId, selectedServiceForAttribute, timeSliceWindow]);

  return dataState;
};